import { Spacing } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import AccountStatusSticker from 'dashboard/components/AccountStatusSticker';
import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import Pii from 'dashboard/components/Pii';
import { MonoSpan } from 'dashboard/components/Typography';
import TableLayout, { TableLinkTR, TableTD, TableTH } from 'dashboard/layouts/TableLayout';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Account } from 'dashboard/models/account';
import { AccountStatus } from 'dashboard/models/account-status';
import { AccountTypesFilterOption } from 'dashboard/models/account-type';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import NullStateAccounts from 'images/dashboard/i16-briefcase.svg';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

const AccountsTableContainer = styled('div')`
  margin-bottom: ${Spacing[2]};
`;

interface AccountsTableProps {
  accountStatuses: AccountStatus[];
  accountTypesFilterOptions: AccountTypesFilterOption[];
  accounts: Account[];
  canSort: boolean;
  dataTest?: string;
  featureFlags: FeatureFlagsMap;
  handleSortUpdate: (sortParams: SortParams) => void;
  hasUsedFilters: boolean;
  loading: boolean;
  sortParams: SortParams;
}

const AccountsTable = ({
  accountStatuses = [],
  accountTypesFilterOptions = [],
  accounts = [],
  canSort,
  dataTest,
  featureFlags,
  handleSortUpdate,
  hasUsedFilters,
  loading,
  sortParams,
}: AccountsTableProps) => {
  const handleColumnHeaderClick = (columnName: string) => {
    if (!canSort) {
      return;
    }

    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  const accountRows = accounts.map((account, key) => {
    const accountStatus = accountStatuses.find((actst) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return actst.id === account.relationships['account-status'].data.id;
    });
    const accountTypeFilterOption = accountTypesFilterOptions.find((filterOption) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return filterOption.id === account.relationships['account-type'].data.id;
    });
    return (
      <AccountTableRow
        account={account}
        accountStatus={accountStatus}
        accountTypeFilterOption={accountTypeFilterOption}
        featureFlags={featureFlags}
        key={key}
      />
    );
  });

  return (
    <AccountsTableContainer data-test={dataTest}>
      <TableLayout
        dataTest='accounts-table__table'
        isEmpty={accounts.length === 0}
        isLoading={loading}
        showHeader={hasUsedFilters}
        headerTR={
          <>
            <TableTH
              isSorted={sortParams.sort === 'name_full'}
              canSort={canSort}
              isDescending={sortParams.shouldSortDescending}
              id='accounts-table__header--name_full'
              data-test='accounts-table__header--name_full'
              onClick={() => handleColumnHeaderClick('name_full')}
            >
              Name
            </TableTH>
            <TableTH id='accounts-table__header--id' data-test='accounts-table__header--id'>
              Account ID
            </TableTH>
            <TableTH
              isSorted={sortParams.sort === 'reference_id'}
              canSort={canSort}
              isDescending={sortParams.shouldSortDescending}
              id='accounts-table__header--reference-id'
              data-test='accounts-table__header--reference-id'
              onClick={() => handleColumnHeaderClick('reference_id')}
            >
              Reference ID
            </TableTH>
            {featureFlags['dashboard:accounts-show-account-status'] && (
              <TableTH
                canSort={canSort}
                isDescending={sortParams.shouldSortDescending}
                id='accounts-table__header--account-status'
                data-test='accounts-table__header--account-status'
                onClick={() => handleColumnHeaderClick('account_status')}
              >
                Status
              </TableTH>
            )}
            {featureFlags['dashboard:accounts-show-account-type'] && (
              <TableTH
                canSort={canSort}
                isDescending={sortParams.shouldSortDescending}
                id='accounts-table__header--account-type'
                data-test='accounts-table__header--account-type'
                onClick={() => handleColumnHeaderClick('account_type')}
              >
                Type
              </TableTH>
            )}
            <TableTH
              canSort={canSort}
              isSorted={sortParams.sort === 'updated_at'}
              isDescending={sortParams.shouldSortDescending}
              id='accounts-table__header--updated-at'
              data-test='accounts-table__header--updated-at'
              onClick={() => handleColumnHeaderClick('updated_at')}
            >
              {`Last updated at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
            </TableTH>
          </>
        }
        nullState={
          <TableNullState hasUsedFilters={hasUsedFilters} dataTestBlock='accounts-view'>
            <NullStateAccounts />
            <Spacer height={3} />
            <div>
              Accounts mirror the users on your system. Create an account or inquiry to view an
              account.
            </div>
            <Spacer height={5} />
            <a
              href='https://support.withpersona.com/hc/en-us/articles/360059119913-Accounts-Overview'
              target='_blank'
              rel='noreferrer'
            >
              Learn more about accounts
            </a>
          </TableNullState>
        }
      >
        {accountRows}
      </TableLayout>
    </AccountsTableContainer>
  );
};

interface AccountTableRowProps {
  account: Account;
  accountStatus?: AccountStatus;
  accountTypeFilterOption?: AccountTypesFilterOption;
  featureFlags: FeatureFlagsMap;
}

const AccountTableRow = ({
  account,
  accountStatus,
  accountTypeFilterOption,
  featureFlags,
}: AccountTableRowProps) => {
  const {
    'updated-at': updatedAt,
    'name-full': nameFull,
    'name-full-localized': nameFullLocalized,
    'reference-id': referenceId,
    'redacted-at': redactedAt,
  } = account.attributes;

  return (
    <TableLinkTR
      to={`/dashboard/accounts/${account.id}`}
      data-test={`accounts-table-row__${account.id}`}
      id={`accounts-table-row__${account.id}`}
    >
      <TableTD>
        <Pii isRedacted={redactedAt != null}>
          {nameFullLocalized ?? nameFull ?? <Paragraph color='disabled'>No name</Paragraph>}
        </Pii>
      </TableTD>
      <TableTD>
        <HoverCopyableField copyValue={account.id} confirmationText='Copied Account ID'>
          <MonoSpan>{account.id}</MonoSpan>
        </HoverCopyableField>
      </TableTD>
      <TableTD>
        {referenceId ? (
          <MonoSpan>{referenceId}</MonoSpan>
        ) : (
          <Paragraph color='disabled'>No reference ID</Paragraph>
        )}
      </TableTD>
      {featureFlags['dashboard:accounts-show-account-status'] && (
        <TableTD>
          {accountStatus?.attributes?.name && (
            <AccountStatusSticker accountStatus={accountStatus} />
          )}
        </TableTD>
      )}
      {featureFlags['dashboard:accounts-show-account-type'] && (
        <TableTD>
          {accountTypeFilterOption?.name && <MonoSpan>{accountTypeFilterOption.name}</MonoSpan>}
        </TableTD>
      )}
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <TableTD>{DateTime.fromISO(updatedAt).toFormat(DATE_AND_TIME_FORMAT)}</TableTD>
    </TableLinkTR>
  );
};

export default AccountsTable;
